import {
  IOngoingReporting,
  IOngoingReportingData,
  IOngoingReportingRawMapping,
  IOngoingReportingRawMappingData,
  IOngoingReportingReconciliationRow,
  IOngoingReportingSummaryData,
  IOngoingReportingSummaryHeaders,
  IOngoingReportingUpdatesItem,
  IInventoryMappingFieldsData,
  ISalesBySKUVisualizationData,
  OngoingReportingType,
  ISalesBySKUVisualizationFilters,
  ISalesDistributionAnalysisData,
  IOngoingReportingSummaryHeadersData,
  IOngoingReportingSummaryHeaderData,
  IOngoingReportingSummaryDataGrowth,
  FinancialsSummaryKey,
  FinancialsSummaryScope,
} from '@common/interfaces/bbc'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import { PROCESS_CAPTABLE_SUCCESS } from '../capTable/actions'
import { LIST_CLIENT_FINANCIALS_SUMMARY_SUCCESS } from '../clientInfo/actions'
import * as actions from './actions'

type IListPageParams = IPaginationParams & { page?: number; perPage?: number }

/**
 * A scroll page that starts inside the rows already loaded was asked for before a newer read
 * replaced the list (a "Go to" or a refresh landing first), so appending it would duplicate rows.
 */
const isSupersededPage = (loadedLength: number, { page, perPage }: IListPageParams) =>
  page !== undefined && perPage !== undefined && page * perPage < loadedLength

interface ISummarySuccessPayload {
  statementType: FinancialsSummaryKey
  data: IOngoingReportingSummaryData
  headers: string[]
  headersColors: string[]
  headersData: IOngoingReportingSummaryHeaderData[]
  growth: IOngoingReportingSummaryDataGrowth
  reconciliation?: IOngoingReportingReconciliationRow[]
}

export type FinancialsDrillDownKey = OngoingReportingType | FinancialsSummaryScope

export interface IFinancialsDrillDownState {
  // `params` of the most recent drill-down request per statement type, used by
  // identity to tell the latest response apart from stale (out-of-order) ones.
  pendingParams: Partial<Record<FinancialsDrillDownKey, object>>
  data: Partial<Record<FinancialsDrillDownKey, IOngoingReportingRawMappingData>>
}

type ISummarySuccessAction = IActionWithParams<{ summaryScope?: FinancialsSummaryScope }>

const toSummaryKey = (action: ISummarySuccessAction): FinancialsSummaryKey =>
  action.params?.summaryScope ?? (action.data as ISummarySuccessPayload).statementType

const FINANCIALS_DRILL_DOWN_INITIAL_STATE: IFinancialsDrillDownState = {
  pendingParams: {},
  data: {},
}

const ongoingReportingReducer = combineReducers({
  /** The newest read of the mapping list from its top, which a slower, older one must not undo. */
  latestConsolidatedReadId(
    state: number | null = null,
    action: IActionWithParams<IListPageParams>,
  ) {
    if (
      action.type === actions.LIST_CONSOLIDATED_ONGOING_REPORTING_REQUEST &&
      !action.params?.loadMore
    ) {
      return action.requestId ?? state
    }
    return state
  },
  loadingArray: loadingArrayReducer(
    [actions.prefix],
    [actions.LIST_ONGOING_REPORTING_REQUEST, actions.LIST_INVENTORY_MAPPING_REQUEST],
  ),
  data: createLoadingDataReducer<IOngoingReportingData>(
    [actions.LIST_ONGOING_REPORTING_SUCCESS],
    [actions.UPDATE_ONGOING_REPORTING_STATUS_SUCCESS, actions.UPDATE_ONGOING_REPORTING_SUCCESS],
  ),
  skuDetails: createLoadingDataReducer<IInventoryMappingFieldsData>(
    [actions.LIST_INVENTORY_MAPPING_REQUEST],
    [actions.UPDATE_INVENTORY_MAPPING_REQUEST],
  ),
  skuVisualization: createLoadingDataReducer<ISalesBySKUVisualizationData>(
    [actions.LIST_SALES_BY_SKU_VISUALIZATION_REQUEST],
    [actions.EXPORT_SALES_BY_SKU_VISUALIZATION_REQUEST],
    false,
    [actions.HIDE_SALES_DATA],
  ),
  skuVisualizationsFilters: createLoadingDataReducer<ISalesBySKUVisualizationFilters>(
    [actions.LIST_SALES_BY_SKU_FILTERS_REQUEST],
    [],
    false,
    [actions.HIDE_SALES_DATA],
  ),
  ongoingReporting(state: IOngoingReporting = null, action: IAction): IOngoingReporting {
    switch (action.type) {
      case PROCESS_CAPTABLE_SUCCESS:
      case actions.SHOW_ONGOING_REPORTING_SUCCESS:
      case actions.SUBMIT_ONGOING_REPORTING_SUCCESS:
        return action.data as IOngoingReporting
      case actions.UPDATE_ONGOING_REPORTING_STATUS_SUCCESS:
      case actions.UPDATE_ONGOING_REPORTING_AS_OF_DATE_SUCCESS: {
        const data = action.data as IOngoingReporting
        if (!state) {
          return data
        }
        return {
          ...state,
          ...data,
        }
      }
      case actions.HIDE_ONGOING_REPORTING:
        return null
      default:
        return state
    }
  },
  dataConsolidated(
    state: IOngoingReportingRawMappingData = null,
    action: IActionWithParams<IListPageParams>,
  ): IOngoingReportingRawMappingData {
    switch (action.type) {
      case actions.UPDATE_CONSOLIDATED_ONGOING_REPORTING_SUCCESS: {
        const updatedData = action.data as IOngoingReportingRawMapping[]
        return {
          ...state,
          data: state.data.map((item) =>
            updatedData.find(({ id }) => id === item.id)
              ? {
                  ...item,
                  ...(updatedData.find(({ id }) => id === item.id) || {}),
                }
              : item,
          ),
        }
      }
      case actions.LIST_CONSOLIDATED_ONGOING_REPORTING_SUCCESS: {
        const data = action.data as IOngoingReportingRawMappingData
        if (!action.params?.loadMore) {
          return data
        }
        if (isSupersededPage(state?.data?.length ?? 0, action.params)) {
          return state
        }
        return {
          ...state,
          data: [...state.data, ...data.data],
        }
      }
      default:
        return state
    }
  },
  dataConsolidatedMultipleReports(
    state: Partial<Record<OngoingReportingType, IOngoingReportingRawMappingData>> = {},
    action: IActionWithParams<IListPageParams>,
  ): Partial<Record<OngoingReportingType, IOngoingReportingRawMappingData>> {
    switch (action.type) {
      case actions.LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS_SUCCESS: {
        const data = action.data as IOngoingReportingRawMappingData & {
          statementType: OngoingReportingType
        }
        if (!action.params?.loadMore) {
          return {
            ...state,
            [data.statementType]: data,
          }
        }
        const currentData = state[data.statementType]
        if (isSupersededPage(currentData?.data?.length ?? 0, action.params)) {
          return state
        }
        return {
          ...state,
          [data.statementType]: {
            ...currentData,
            data: [...(currentData?.data || []), ...data.data],
          },
        }
      }
      default:
        return state
    }
  },
  financialsDrillDown(
    state: IFinancialsDrillDownState = FINANCIALS_DRILL_DOWN_INITIAL_STATE,
    action: IActionWithParams<{
      statementType?: OngoingReportingType
      summaryScope?: FinancialsSummaryScope
    }>,
  ): IFinancialsDrillDownState {
    const drillDownKey = action.params?.summaryScope ?? action.params?.statementType
    switch (action.type) {
      case actions.LIST_FINANCIALS_DRILL_DOWN_REQUEST: {
        if (!drillDownKey) {
          return state
        }
        return {
          ...state,
          pendingParams: { ...state.pendingParams, [drillDownKey]: action.params },
        }
      }
      case actions.LIST_FINANCIALS_DRILL_DOWN_SUCCESS: {
        // Last-request-wins: only the response to the most recent request for
        // this slot may be stored. The api middleware dispatches
        // REQUEST and SUCCESS with the same `params` reference, so an identity
        // mismatch means this response is stale (out-of-order) — discard it.
        if (!drillDownKey || state.pendingParams[drillDownKey] !== action.params) {
          return state
        }
        const data = action.data as IOngoingReportingRawMappingData
        return {
          ...state,
          data: { ...state.data, [drillDownKey]: data },
        }
      }
      case actions.HIDE_FINANCIALS_DRILL_DOWN: {
        if (!drillDownKey) {
          return state
        }
        const data = { ...state.data }
        delete data[drillDownKey]
        // Also drop the pending request marker so an in-flight response for
        // the cleared statement type cannot land after the clear.
        const pendingParams = { ...state.pendingParams }
        delete pendingParams[drillDownKey]
        return { data, pendingParams }
      }
      default:
        return state
    }
  },
  dataSummary(
    state: Partial<Record<FinancialsSummaryKey, IOngoingReportingSummaryData>> = {},
    action: ISummarySuccessAction,
  ): Partial<Record<FinancialsSummaryKey, IOngoingReportingSummaryData>> {
    switch (action.type) {
      case actions.LIST_SUMMARY_ONGOING_REPORTING_SUCCESS:
      case LIST_CLIENT_FINANCIALS_SUMMARY_SUCCESS: {
        const data = action.data as ISummarySuccessPayload
        return {
          ...state,
          [toSummaryKey(action)]: data.data,
        }
      }
      default:
        return state
    }
  },
  // Keyed as the summary is, so a scoped read never overwrites the statement's own rows.
  // Not fed by LIST_CLIENT_FINANCIALS_SUMMARY, whose persisted statement has no reported totals.
  dataSummaryReconciliation(
    state: Partial<Record<FinancialsSummaryKey, IOngoingReportingReconciliationRow[]>> = {},
    action: ISummarySuccessAction,
  ): Partial<Record<FinancialsSummaryKey, IOngoingReportingReconciliationRow[]>> {
    switch (action.type) {
      case actions.LIST_SUMMARY_ONGOING_REPORTING_SUCCESS: {
        const data = action.data as ISummarySuccessPayload
        return {
          ...state,
          [toSummaryKey(action)]: data.reconciliation || [],
        }
      }
      default:
        return state
    }
  },
  dataSummaryHeaders(
    state: IOngoingReportingSummaryHeaders = null,
    action: ISummarySuccessAction,
  ): IOngoingReportingSummaryHeaders {
    switch (action.type) {
      case actions.LIST_SUMMARY_ONGOING_REPORTING_SUCCESS:
      case LIST_CLIENT_FINANCIALS_SUMMARY_SUCCESS: {
        const data = action.data as ISummarySuccessPayload
        return {
          ...state,
          [toSummaryKey(action)]: data.headers,
        }
      }
      default:
        return state
    }
  },
  dataSummaryHeadersColors(
    state: IOngoingReportingSummaryHeaders = null,
    action: ISummarySuccessAction,
  ): IOngoingReportingSummaryHeaders {
    switch (action.type) {
      case actions.LIST_SUMMARY_ONGOING_REPORTING_SUCCESS:
      case LIST_CLIENT_FINANCIALS_SUMMARY_SUCCESS: {
        const data = action.data as ISummarySuccessPayload
        return {
          ...state,
          [toSummaryKey(action)]: data.headersColors,
        }
      }
      default:
        return state
    }
  },
  dataSummaryHeadersData(
    state: IOngoingReportingSummaryHeadersData = null,
    action: ISummarySuccessAction,
  ): IOngoingReportingSummaryHeadersData {
    switch (action.type) {
      case actions.LIST_SUMMARY_ONGOING_REPORTING_SUCCESS:
      case LIST_CLIENT_FINANCIALS_SUMMARY_SUCCESS: {
        const data = action.data as ISummarySuccessPayload
        return {
          ...state,
          [toSummaryKey(action)]: data.headersData,
        }
      }
      default:
        return state
    }
  },
  dataSummaryGrowth(
    state: Partial<Record<FinancialsSummaryKey, IOngoingReportingSummaryDataGrowth>> = {},
    action: ISummarySuccessAction,
  ): Partial<Record<FinancialsSummaryKey, IOngoingReportingSummaryDataGrowth>> {
    switch (action.type) {
      case actions.LIST_SUMMARY_ONGOING_REPORTING_SUCCESS:
      case LIST_CLIENT_FINANCIALS_SUMMARY_SUCCESS: {
        const data = action.data as ISummarySuccessPayload
        return {
          ...state,
          [toSummaryKey(action)]: data.growth,
        }
      }
      default:
        return state
    }
  },
  reportingUpdatesList(
    state: IOngoingReportingUpdatesItem[] = [],
    action: IAction & {
      error?: { code?: string; data?: IOngoingReportingUpdatesItem[] }
    },
  ): IOngoingReportingUpdatesItem[] {
    switch (action.type) {
      case actions.SUBMIT_ONGOING_REPORTING_REQUEST:
        return []
      case actions.SUBMIT_ONGOING_REPORTING_FAILURE:
        if (action.error?.code === 'CUSTOM_ERROR') {
          return action.error.data || []
        }
        return state
      default:
        return state
    }
  },
  salesByDistributionGraph: createLoadingDataReducer<ISalesDistributionAnalysisData>(
    [actions.LIST_SALES_BY_DISTRIBUTION_GRAPH_REQUEST],
    [],
    false,
    [actions.HIDE_SALES_DATA],
  ),
})

type IOngoingReportingState = ReturnType<typeof ongoingReportingReducer>

/**
 * A list read from the top answered after a newer one was asked for: it was computed before
 * what came since (lines marked as reviewed, say), so landing it would show a stale count.
 */
const isStaleConsolidatedRead = (
  state: IOngoingReportingState | undefined,
  action: IActionWithParams<IListPageParams>,
) =>
  action.type === actions.LIST_CONSOLIDATED_ONGOING_REPORTING_SUCCESS &&
  !action.params?.loadMore &&
  action.requestId !== undefined &&
  state?.latestConsolidatedReadId != null &&
  action.requestId < state.latestConsolidatedReadId

const reducer = (
  state: IOngoingReportingState | undefined,
  action: IActionWithParams<IListPageParams>,
): IOngoingReportingState =>
  state && isStaleConsolidatedRead(state, action) ? state : ongoingReportingReducer(state, action)

export default reducer
